import { useTranslation } from 'react-i18next';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { CatalogEntry, useBusStations } from '../queries';
import { useAutocompleteFocus } from '../useAutocompleteFocus';

/**
 * La MISMA etiqueta para la lista y para el input fuera de la sesión de
 * tipeo (#2226): si difieren, MUI repone el input contra la suya y pisa lo
 * que muestra este componente.
 */
function stationLabel(station: CatalogEntry): string {
  return station.countryCode ? `${station.name} (${station.countryCode})` : station.name;
}

interface Props {
  label: string;
  value: CatalogEntry | null;
  onChange: (station: CatalogEntry | null) => void;
  width?: number;
}

/**
 * Parada de micro con autocomplete contra el catálogo del hub. Búsqueda remota
 * (`q` filtra en el hub: son 1.392 paradas, más que el cap de lista completa).
 * En la búsqueda viaja el CÓDIGO de la parada (`id`), que es inequívoco; el
 * hub también acepta texto libre, pero eso queda para deep links a mano.
 */
export function BusStationInput({ label, value, onChange, width = 240 }: Props) {
  const { t } = useTranslation();
  const [debouncedQuery, setQuery] = useDebouncedState('', 350);
  const focus = useAutocompleteFocus({
    selectedLabel: value ? stationLabel(value) : '',
    onQueryChange: setQuery,
  });
  const { data: stations, isFetching } = useBusStations(debouncedQuery);
  // Aislado (#2226): solo se enciende mientras el asesor tipea EN ESTE campo.
  const showLoading = focus.editing && isFetching && debouncedQuery.trim().length >= 2;

  return (
    <Autocomplete
      size="small"
      sx={{ width: '100%', maxWidth: width }}
      options={stations ?? []}
      loading={showLoading}
      value={value}
      onChange={(_event, station, reason) => {
        if (focus.commitValueChange(reason)) onChange(station);
      }}
      inputValue={focus.inputValue}
      onFocus={focus.onFocus}
      onBlur={focus.onBlur}
      onInputChange={focus.onInputChange}
      getOptionLabel={stationLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      // El filtrado ya lo hizo el hub: re-filtrar por el label esconde paradas.
      filterOptions={(opts) => opts}
      noOptionsText={
        debouncedQuery.trim().length < 2
          ? t('travelServices.buses.typeToSearch')
          : t('travelServices.buses.noStations')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('travelServices.buses.stationPlaceholder')}
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {showLoading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
